import mixpanel, { Dict } from 'mixpanel-browser';
import {
  ChangeEventHandler,
  Children,
  cloneElement,
  FC,
  FocusEvent,
  FocusEventHandler,
  isValidElement,
  PropsWithChildren,
  useRef,
  useState,
} from 'react';
import { analyticsSafeSearch, analyticsSafeUrl } from '../utils/urlPrivacy';

mixpanel.init(`${process.env.REACT_APP_MIXPANEL_TOKEN}`);

/**
 * mixpanel-browser adds the page URL to every event on its own: `$current_url` is
 * `location.href`, `$referrer` is `document.referrer`, and `$initial_referrer` is the first
 * referrer it saw, kept in its cookie. With hash routing, `location.href` carries the route's
 * query, and the Documents search puts a patient's name there (`searchPatientName`). Properties
 * passed to `track` win over the SDK's own, so these replace them with the allow-listed URL.
 */
export const sdkUrlProperties = (): Dict => {
  const properties: Dict = { $current_url: analyticsSafeUrl(window.location.href) };
  if (document.referrer) {
    properties.$referrer = analyticsSafeUrl(document.referrer);
  }
  // Before the first event the SDK has not stored it yet; it then stores `document.referrer`.
  const initialReferrer = mixpanel.get_property('$initial_referrer') ?? document.referrer;
  if (typeof initialReferrer === 'string' && initialReferrer && initialReferrer !== '$direct') {
    properties.$initial_referrer = analyticsSafeUrl(initialReferrer);
  }
  return properties;
};

/**
 * A form's shape with every value replaced by whether it is filled in. The Task form holds a
 * patient's name, date of birth and member id, and the Insurance plan form has free-text notes,
 * so their save events report which fields were filled, never what was in them.
 */
export const formFieldPresence = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.length ? value.map(formFieldPresence) : false;
  }
  if (value !== null && typeof value === 'object' && !(value instanceof Date)) {
    return Object.keys(value).reduce<Dict>((presence, key) => {
      presence[key] = formFieldPresence((value as Dict)[key]);
      return presence;
    }, {});
  }
  return value !== undefined && value !== null && value !== '';
};

type ChildrenWithEventHandlersProps = {
  onChange: ChangeEventHandler<HTMLInputElement>;
  onBlur: FocusEventHandler<HTMLInputElement>;
  onFocus: FocusEventHandler<HTMLInputElement>;
};

export type TrackableElementTypes = 'input' | 'checkbox';

export const TrackableElement: FC<
  PropsWithChildren<{
    properties: { Section: string; Name?: string };
    callback?: Function;
    type?: TrackableElementTypes;
    /** Leave the field's old and new values out of the event, for free text that may be PHI. */
    redactValues?: boolean;
  }>
> = ({ children, properties, callback, type, redactValues }) => {
  const [oldValue, setOldValue] = useState<string>();
  const [focused, setFocused] = useState<boolean>(false);

  const originalValue = useRef<string | undefined>(undefined);

  const getTarget = (event: FocusEvent<HTMLInputElement>): HTMLInputElement | undefined => {
    const { target } = event;
    if (target.tagName !== 'INPUT' && target.nextElementSibling) {
      const sibling = target.nextElementSibling;
      if (sibling.tagName !== 'INPUT') return undefined;
      return sibling as HTMLInputElement;
    }
    return target;
  };

  const onBlur = (event: FocusEvent<HTMLInputElement>) => {
    const { value, name } = getTarget(event) ?? {};
    const parsedValue = !!value?.toString ? value.toString() : value;
    if (parsedValue !== oldValue) {
      if (originalValue.current === undefined) originalValue.current = oldValue;

      // A free-text field can hold a patient's name or member id: report the edit, not the text.
      MixpanelService.track('Field Was Edited', {
        Name: name,
        ...(redactValues ? {} : { 'New Value': value, 'Old Value': oldValue }),
        Section: properties.Section,
      });
    }

    setFocused(false);
  };

  const onChangeCheckbox = (event: any) => {
    const checked = event && event.length === 1;

    if (originalValue.current === undefined) originalValue.current = oldValue;

    MixpanelService.track('Field Was Edited', {
      Name: properties.Name,
      'New Value': checked,
      'Old Value': !checked,
      Section: properties.Section,
    });

    callback?.({
      name: properties.Name,
      newValue: checked,
      originalValue: originalValue.current,
    });
  };

  const onFocus = (event: FocusEvent<HTMLInputElement>) => {
    const { value } = getTarget(event) ?? {};
    if (value !== undefined && !focused) {
      setOldValue(value);
      setFocused(true);
    }
  };

  const childrenWithEventHandlers = Children.map(children, (child) => {
    if (isValidElement<ChildrenWithEventHandlersProps>(child)) {
      if (type === 'checkbox') {
        return cloneElement(child, { onChange: onChangeCheckbox });
      }
      return cloneElement(child, { onBlur, onFocus });
    }
    return child;
  });

  return <>{childrenWithEventHandlers}</>;
};

export default class MixpanelService {
  public static track(eventName: string, properties?: Dict) {
    if (process.env.NODE_ENV === 'production') {
      mixpanel.track(eventName, { ...properties, ...sdkUrlProperties() });
    } else {
      console.log('[MIXPANEL]', eventName, properties);
    }
  }

  /**
   * General use
   */

  /**
   * The query is reduced to its PHI-safe parameters before it is sent: the Referrals and
   * Documents searches put a patient's name in the URL, and every navigation lands here.
   */
  public static trackPageView({ pathname, search }: { pathname?: string; search?: string }) {
    MixpanelService.track('Page View', { Route: pathname, Search: analyticsSafeSearch(search) });
  }

  public static trackButtonClicked(properties?: Dict) {
    MixpanelService.track('Button Clicked', properties);
  }

  /**
   * The Eligibility tasks Search button. The Name and Member ID filters are free text holding a
   * patient's name or member id, so they are sent as presence flags. Status and Source are fixed
   * options and the dates are the task's requested dates, so those keep their values.
   */
  public static trackTaskSearch(filters: {
    name: string;
    status: string[];
    source: string[];
    requestedDateFrom: string | null;
    requestedDateTo: string | null;
    memberId: string;
    next48hr: boolean;
  }) {
    MixpanelService.trackButtonClicked({
      Section: 'Task Filter',
      Name: 'Search',
      Value: {
        HasName: !!filters.name,
        Status: filters.status,
        Source: filters.source,
        FromRequiredDate: filters.requestedDateFrom,
        ToRequiredDate: filters.requestedDateTo,
        HasMemberId: !!filters.memberId,
        Next48hr: filters.next48hr,
      },
    });
  }

  public static trackSearch(search: string, path: string) {
    if (search === '') {
      return;
    }

    const tabName = MixpanelService.getTabNameFromPath(path);
    MixpanelService.track('Search bar usage', {
      Tab: tabName,
      Search: search,
    });
  }

  public static trackCreate(path: string) {
    const tabName = MixpanelService.getTabNameFromPath(path);
    MixpanelService.track('Create', {
      Field: 'Create ' + tabName,
      Tab: tabName,
      Type: 'link',
      source: path,
    });
  }

  private static getTabNameFromPath(path: string): string {
    const routes: string[] = path.split('/');
    const tabName: string = routes[routes.length - 1];

    const modifiedTabName = tabName
      .split('-')
      .map((word) => `${word[0].toUpperCase()}${word.slice(1)}`)
      .join(' ');

    return modifiedTabName;
  }
}
